/**
 * PATH: packages/ui/src/versionWatcher/reloadPolicy.ts
 * FILE_ID: UI-VERSION-POLICY-001
 * PURPOSE: Pure decision helpers for VersionWatcher's auto-reload UX.
 * WHY:
 *   The previous reload UX was operator-hostile: when a new deploy
 *   landed, we showed a small bottom-centre pill and waited for the
 *   operator to click "Reload now". They asked (2026-05-08):
 *   *"why do I have to hard refresh, this is user unfriendly"* —
 *   and they were right. Two extra triggers fix the worst cases:
 *
 *     1. shouldReloadOnVisibilityRegain — the operator left the tab,
 *        we deployed, they came back. That's the moment they expect
 *        fresh content. Reload silently when away time exceeded
 *        threshold.
 *     2. tickCountdown — when the operator IS active and a new
 *        version arrives, instead of nagging them to click, the
 *        toast shows "Reloading in 30s · Cancel" and reloads
 *        automatically. Cancellable, so it never interrupts a
 *        focused task without consent.
 *
 *   Putting these decisions in pure helpers keeps the React
 *   component small and lets us unit-test every branch.
 *
 * FLOW:
 *   shouldReloadOnVisibilityRegain(awayMs, thresholdMs) → bool
 *   tickCountdown(prev, deltaMs)                       → next state
 */

/**
 * Decide whether to silently reload the page when the operator
 * returns to a hidden tab.
 *
 * Conditions: an update is pending AND the tab was hidden for at
 * least `thresholdMs`. Threshold defaults are picked by the host
 * app — SAM uses 2 min so a quick alt-tab to slack doesn't trigger
 * a reload mid-thought.
 *
 * Returns false when policy is disabled (thresholdMs == null) so
 * the host can opt-out without conditional wiring.
 */
export function shouldReloadOnVisibilityRegain(
  awayMs: number,
  thresholdMs: number | null,
): boolean {
  if (thresholdMs == null) return false
  if (!Number.isFinite(awayMs) || awayMs < 0) return false
  return awayMs >= thresholdMs
}

export interface CountdownState {
  /** Remaining milliseconds. Null when no countdown is active. */
  remainingMs: number | null
  /** True after the operator clicked Cancel — countdown will not
   *  restart for the current detected update, even if the polling
   *  loop re-confirms the same version. Resets when a brand-new
   *  version is detected (handled by the host effect). */
  cancelled: boolean
}

export const INITIAL_COUNTDOWN_STATE: CountdownState = {
  remainingMs: null,
  cancelled: false,
}

/**
 * Start (or reset) a countdown when an update has just been detected.
 * Caller is responsible for guarding against duplicate calls per
 * detection — see VersionWatcher.tsx where this is gated by the
 * `lastAnnouncedVersion` from DismissalState.
 *
 * If the operator has already cancelled this version's countdown
 * we honour that decision (no countdown). When `durationMs` is
 * null/undefined the feature is disabled.
 */
export function startCountdown(
  prev: CountdownState,
  durationMs: number | null,
): CountdownState {
  if (durationMs == null || durationMs <= 0) return prev
  if (prev.cancelled) return prev
  return { remainingMs: durationMs, cancelled: false }
}

/**
 * Advance the countdown by `deltaMs`. Returns the next state plus
 * a `expired` flag the caller checks to trigger window.location.reload().
 *
 * Truth table:
 *   prev.remainingMs   deltaMs    → next.remainingMs / expired
 *   ───────────────    ────────   ──────────────────────────────
 *   null               *           null / false   (no countdown)
 *   N (N > delta)      delta       N - delta / false
 *   N (N ≤ delta)      delta       0 / true       (fire reload)
 */
export function tickCountdown(
  prev: CountdownState,
  deltaMs: number,
): { next: CountdownState; expired: boolean } {
  if (prev.remainingMs == null) return { next: prev, expired: false }
  if (!Number.isFinite(deltaMs) || deltaMs < 0) {
    return { next: prev, expired: false }
  }
  const remaining = prev.remainingMs - deltaMs
  if (remaining <= 0) {
    return { next: { ...prev, remainingMs: 0 }, expired: true }
  }
  return { next: { ...prev, remainingMs: remaining }, expired: false }
}

/**
 * Operator clicked Cancel on the toast — halt the countdown and
 * remember the choice so the polling loop doesn't restart it on
 * the next 30s tick.
 */
export function cancelCountdown(prev: CountdownState): CountdownState {
  return { remainingMs: null, cancelled: true }
}

/**
 * Reset the countdown machine when a brand-new version is detected
 * (different from the one whose countdown was previously cancelled).
 * Without this, an operator who cancels deploy A's countdown would
 * never see deploy B's — exactly the same class of bug we just
 * fixed for the dismissal flag.
 */
export function resetCountdown(_prev: CountdownState): CountdownState {
  return { ...INITIAL_COUNTDOWN_STATE }
}

/**
 * Format remaining ms for the toast: "30s", "5s", "1s". Caller
 * shows this beside "Reloading in" so operators know exactly how
 * much time they have to click Cancel. Returns empty string when
 * no countdown is active.
 */
export function formatCountdownLabel(remainingMs: number | null): string {
  if (remainingMs == null) return ''
  const seconds = Math.max(0, Math.ceil(remainingMs / 1000))
  return `${seconds}s`
}
